import { useEffect, useState } from "react";
import { useSwipeable } from "react-swipeable";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";

export interface ICaseStudy {
  title: string;
  images: string[];
}

export interface IProject {
  pageTitle: string;
}

const Cases: React.FC<IProject> = (project: IProject) => {
  const [caseStudies, setCaseStudies] = useState<ICaseStudy[]>([]);
  const [current, setCurrent] = useState(0);
  let url = encodeURI(`${project.pageTitle}data.json`);

  const count = caseStudies.length;
  const next = () =>
    setCurrent((c) => (count ? (c + 1) % count : 0));
  const prev = () =>
    setCurrent((c) => (count ? (c - 1 + count) % count : 0));

  useEffect(() => {
    if (count === 0) return;
    const timer = setInterval(
      () => setCurrent((c) => (c + 1) % count),
      5000
    );
    return () => clearInterval(timer);
  }, [count]);

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => next(),
    onSwipedRight: () => prev(),
    trackMouse: true,
  });

  const getCaseStudies = async () => {
    const caseStudyData = await fetch(url, {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    }).then(function (response) {
      return response.json();
    });
    setCaseStudies(caseStudyData as unknown as ICaseStudy[]);
  };

  useEffect(() => {
    getCaseStudies();
  }, []);

  const openProject = (idx: number) => {
    window.location.hash = `#/project/${encodeURIComponent(
      project.pageTitle
    )}/${encodeURIComponent(caseStudies[idx].title)}`;
  };

  return (
    <section id={project.pageTitle}>
      <div className="w-full relative">
        <div className="absolute left-0 top-0 w-full overflow-hidden"></div>
        <div className="max-w-7xl mx-4 lg:mx-auto pt-10 lg:pt-10">
          <h1 className="text-h-gold text-4xl lg:text-6xl font-normal uppercase tracking-[0.2em] text-center">
            {project.pageTitle}
          </h1>
          <div className="mx-auto pt-16 pb-8 max-w-4xl" {...swipeHandlers}>
            {count > 0 ? (
              <>
                <div className="relative">
                  <button
                    onClick={() => openProject(current)}
                    className="w-full cursor-pointer"
                  >
                    <div className="h-72 sm:h-96 lg:h-[32rem]">
                      <img
                        src={caseStudies[current].images[0]}
                        alt={caseStudies[current].title}
                        className="h-full w-full object-cover overflow-hidden rounded"
                      />
                    </div>
                    <div className="p-4 shadow-lg w-11/12 mx-auto -mt-8 bg-h-navy border border-h-gold rounded z-30 relative">
                      <p className="font-glacial font-medium text-h-gold text-center text-sm sm:text-base uppercase tracking-[0.15em]">
                        {caseStudies[current].title}
                      </p>
                    </div>
                  </button>
                  <button
                    onClick={prev}
                    aria-label="Previous project"
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-h-gold bg-h-navy bg-opacity-60 hover:bg-opacity-90 rounded-full p-2"
                  >
                    <IoIosArrowBack size={28} />
                  </button>
                  <button
                    onClick={next}
                    aria-label="Next project"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-h-gold bg-h-navy bg-opacity-60 hover:bg-opacity-90 rounded-full p-2"
                  >
                    <IoIosArrowForward size={28} />
                  </button>
                </div>
                {count > 1 ? (
                  <img
                    src={caseStudies[(current + 1) % count].images[0]}
                    alt=""
                    aria-hidden="true"
                    className="hidden"
                  />
                ) : null}
                <div className="flex justify-center gap-2 mt-6">
                  {caseStudies.map((article, i) => (
                    <button
                      key={article.title}
                      onClick={() => setCurrent(i)}
                      aria-label={`Show ${article.title}`}
                      className={`w-2.5 h-2.5 rounded-full bg-h-gold ${
                        i === current ? "opacity-100" : "opacity-30"
                      }`}
                    />
                  ))}
                </div>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Cases;
